import React, { FC, ReactNode } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui'
import { ActiveShipmentsSchema, ActiveShipmentsSchemaType } from './schema/ActiveShipmentsSchema'

interface IShipmentFormProps {
  defaultValues: ActiveShipmentsSchemaType
  onFormSubmit: (data: ActiveShipmentsSchemaType) => void
  apiErrors: string[]
  children: ReactNode
  isEditMode: boolean
  isLoading: boolean
  onSetEditMode: (val: boolean) => void
}

/**
 * Update Shipment
 */
const ShipmentForm: FC<IShipmentFormProps> = ({
  apiErrors,
  children,
  defaultValues,
  isEditMode,
  isLoading,
  onFormSubmit,
  onSetEditMode,
}) => {
  const useFormProps = useForm({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(ActiveShipmentsSchema),
  })
  const { errors, formState, handleSubmit, reset } = useFormProps

  const onCancelHandler = () => {
    reset(defaultValues)
    onSetEditMode(false)
  }

  const rowClickHandler = () => onSetEditMode(true)
  const errorMessages = errors.vin?.message || apiErrors?.join(' ')
  const stopErrors = errors.pickupsAttributes?.length || errors.deliveriesAttributes?.length
  const dirtyFieldsCount = Object.values(formState.dirtyFields).length

  return (
    <FormProvider {...useFormProps}>
      <form onSubmit={handleSubmit(onFormSubmit)} data-test='form-shipment'>
        <Box
          padding={3}
          display='flex'
          flexDirection='row'
          role='rowgroup'
          onClick={rowClickHandler}>
          {children}
        </Box>
        {/* Shipment Row Edit State */}
        <Box
          role='row'
          display='flex'
          borderTop={1}
          borderColor={brandColors.coolGray4}
          justifyContent='space-between'
          alignItems='center'
          className={`shipment-row--edit ${isEditMode ? 'isEdit' : ''}`}
          px={3}>
          <Box>
            {errorMessages && (
              <InlineMessage
                dataTest='message-errors'
                type={INLINE_MESSAGE_VARIANTS.ALERT}
                message={errorMessages}
                className='mr2'
              />
            )}
            {stopErrors && (
              <InlineMessage
                dataTest='message-errors'
                type={INLINE_MESSAGE_VARIANTS.ALERT}
                message='Please correct the highlighted stop inputs'
                className='mr2'
              />
            )}
            {dirtyFieldsCount > 0 && !formState.isSubmitSuccessful && (
              <InlineMessage
                dataTest='message-unsaved-changes'
                type={INLINE_MESSAGE_VARIANTS.WARNING}
                message={`${dirtyFieldsCount} unsaved change(s)`}
              />
            )}
          </Box>
          <Box zIndex={3}>
            <Button color='secondary' onClick={onCancelHandler} disabled={isLoading}>
              {dirtyFieldsCount ? 'Discard Changes' : 'Cancel'}
            </Button>
            <Button color='primary' type='submit' disabled={isLoading}>
              {isLoading ? 'Saving Changes ...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  )
}

export default ShipmentForm
